import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

const COL_MAP = {
    '100s': 'hundreds', 'hundreds': 'hundreds',
    '10s': 'tens', 'tens': 'tens',
    '1s': 'ones', 'ones': 'ones',
    '.': 'decimal', 'decimal': 'decimal',
    '1/10': 'tenths', 'tenths': 'tenths',
    '1/100': 'hundredths', 'hundredths': 'hundredths',
    '1/1000': 'thousandths', 'thousandths': 'thousandths'
};

const COLS = [
    { id: 'hundreds', label: '100s', color: 'bg-red-100 border-red-300 text-red-700', power: 2 },
    { id: 'tens', label: '10s', color: 'bg-orange-100 border-orange-300 text-orange-700', power: 1 },
    { id: 'ones', label: '1s', color: 'bg-yellow-100 border-yellow-300 text-yellow-700', power: 0 },
    { id: 'decimal', label: '.', color: 'transparent' },
    { id: 'tenths', label: '1/10', color: 'bg-cyan-100 border-cyan-300 text-cyan-700', power: -1 },
    { id: 'hundredths', label: '1/100', color: 'bg-blue-100 border-blue-300 text-blue-700', power: -2 },
    { id: 'thousandths', label: '1/1000', color: 'bg-indigo-100 border-indigo-300 text-indigo-700', power: -3 },
];

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const roundTo = (n, dp) => {
    const f = Math.pow(10, dp);
    return Math.round(n * f) / f;
};

export const PlaceValueSlider = ({ config = {} }) => {
    const {
        startValue: rawStartValue = 12.34,
        highlightColumn: rawHighlightColumn,
        allowShifting: rawAllowShifting
    } = config;

    const allowShifting = rawAllowShifting ?? true;

    const normalizedHighlight = useMemo(() => {
        if (rawHighlightColumn == null) return undefined;
        const key = String(rawHighlightColumn).trim().toLowerCase();
        return COL_MAP[key]; // only accept known keys/aliases
    }, [rawHighlightColumn]);

    const normalizeStart = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return 12.34;
        // This widget renders to thousandths; keep state stable at 3dp
        return roundTo(clamp(n, -999.999, 999.999), 3);
    };

    const [val, setVal] = useState(() => normalizeStart(rawStartValue));

    // If backend pushes a new startValue (e.g., re-render), keep state in sync.
    useEffect(() => {
        setVal(normalizeStart(rawStartValue));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawStartValue]);

    // Robust digit extraction: work in integer "thousandths" space to avoid float issues.
    const getDigit = (num, power) => {
        const scaled = Math.round(Math.abs(num) * 1000); // thousandths
        const exp = power + 3; // place shift to thousandths space
        const divisor = Math.pow(10, exp);
        return Math.floor(scaled / divisor) % 10;
    };

    const shift = (dir) => {
        const factor = dir === 'left' ? 10 : 0.1;
        setVal(prev => normalizeStart(prev * factor));
    };

    return (
        <div className="flex flex-col gap-6 p-6 bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="flex justify-center items-end gap-1">
                {val < 0 && <div className="text-3xl font-bold text-slate-400 mb-6 mr-2">-</div>}

                {COLS.map((col) => {
                    const isDec = col.id === 'decimal';
                    const isHi = normalizedHighlight === col.id;
                    const digit = isDec ? '.' : getDigit(val, col.power);

                    return (
                        <div key={col.id} className={`flex flex-col items-center ${isDec ? 'w-4' : 'w-16'}`}>
                            <span className="text-xs font-bold text-slate-400 uppercase mb-2 tracking-wider">{col.label}</span>
                            <div
                                className={`
                  flex items-center justify-center h-20 w-full rounded-lg text-3xl font-mono transition-all duration-300
                  ${col.color}
                  ${isHi ? 'ring-4 ring-offset-2 ring-purple-500 scale-110 z-10 shadow-lg' : 'border'}
                  ${isDec ? 'border-none bg-transparent' : 'bg-opacity-50'}
                `}
                            >
                                {digit}
                            </div>
                        </div>
                    );
                })}
            </div>

            {allowShifting && (
                <div className="flex justify-center gap-4 mt-2">
                    <button
                        onClick={() => shift('left')}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 font-medium"
                        type="button"
                    >
                        <ArrowLeftOutlined /> Multiply by 10
                    </button>
                    <button
                        onClick={() => shift('right')}
                        className="flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded hover:bg-pink-100 font-medium"
                        type="button"
                    >
                        Divide by 10 <ArrowRightOutlined />
                    </button>
                </div>
            )}

            <div className="text-center text-slate-500 text-sm">
                Current Value: <span className="font-mono font-bold text-slate-700">{val}</span>
            </div>
        </div>
    );
};
